import apiClient, { clearTokens, setTokens } from "./client";
import { clearRemoteMenuAccess } from "../constants/rbacAccess";
import { loadMenuAccessIfAuthenticated } from "./rbac";
import { hydrateThemeFromUser, applyStoredThemeBodyClasses } from "../utils/themePreferences";
import { clearSessionLockStorage, setLastActivity } from "../utils/sessionLockStorage";

async function completeAuthenticatedSession() {
  const user = await fetchCurrentUser();
  localStorage.setItem("credora_user", JSON.stringify(user));
  localStorage.setItem("Name", user.full_name);
  clearSessionLockStorage();
  setLastActivity();
  hydrateThemeFromUser(user);
  applyStoredThemeBodyClasses();
  await loadMenuAccessIfAuthenticated();
  window.dispatchEvent(new Event("credora-auth-changed"));
  return user;
}

export async function login(email, password, mfaCode) {
  const payload = { email, password };
  if (mfaCode) {
    payload.mfa_code = mfaCode;
  }

  const { data } = await apiClient.post("/auth/login/json", payload);
  if (data.status === "email_2fa_required") {
    return {
      email2faRequired: true,
      challengeId: data.challenge_id,
      emailHint: data.email_hint,
      expiresInMinutes: data.expires_in_minutes,
      message: data.message,
    };
  }

  setTokens(data.access_token, data.refresh_token);
  return completeAuthenticatedSession();
}

export async function verifyEmail2faLogin({ challengeId, code, mfaCode }) {
  const { data } = await apiClient.post("/auth/login/verify-email-2fa", {
    challenge_id: challengeId,
    code,
    mfa_code: mfaCode || undefined,
  });
  setTokens(data.access_token, data.refresh_token);
  return completeAuthenticatedSession();
}

export async function resendEmail2faCode(challengeId) {
  const { data } = await apiClient.post("/auth/login/resend-email-2fa", {
    challenge_id: challengeId,
  });
  return {
    challengeId: data.challenge_id,
    emailHint: data.email_hint,
    expiresInMinutes: data.expires_in_minutes,
    message: data.message,
  };
}

export async function fetchCurrentUser() {
  const { data } = await apiClient.get("/users/me");
  return data;
}

export function getStoredUser() {
  const raw = localStorage.getItem("credora_user");
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function logout() {
  clearTokens();
  localStorage.removeItem("Name");
  localStorage.removeItem("profileURL");
  localStorage.removeItem("authenticated");
  clearSessionLockStorage();
  window.dispatchEvent(new Event("credora-auth-changed"));
}

export async function fetchInvitationPreview(token) {
  const { data } = await apiClient.get("/auth/invitation", { params: { token } });
  return data;
}

export async function acceptInvitation(payload) {
  const { data } = await apiClient.post("/auth/invitation/accept", payload);
  return data;
}

export async function fetchPasswordResetPreview(token) {
  const { data } = await apiClient.get("/auth/password-reset", { params: { token } });
  return data;
}

export async function acceptPasswordReset(payload) {
  const { data } = await apiClient.post("/auth/password-reset/accept", payload);
  return data;
}

export async function unlockSession(password) {
  const { data } = await apiClient.post("/auth/unlock", { password });
  return data;
}
